import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import Meta from '../ass_components/Meta';
import BreadCrumb from '../ass_components/BreadCrumb';
import { translate } from '../i18n';
import { changePasswordApi } from '../servics/authApi';
import { loginSuccess } from '../store/reducers/authSlice';
import { passwordViolation } from '../utils/passwordPolicy';
import { passwordChangeRequired } from '../utils/session';

/** Chemin interne uniquement (pas d'URL absolue ni « //hôte »). */
const safeRedirect = (value) => (value && value.startsWith('/') && !value.startsWith('//') ? value : '/account');

/** Changement du mot de passe de l'utilisateur connecté (urgent s'il n'a jamais été changé). */
const ChangePassword = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const language = useSelector((state) => state.lang?.lang) || 'FR';
    const text = translate('password_change', language) || translate('password_change', 'FR');
    const token = useSelector((state) => state.auth?.token) || localStorage.getItem('token');
    const target = safeRedirect(params.get('redirect'));

    const [values, setValues] = useState({ current: '', next: '', confirm: '' });
    const [errors, setErrors] = useState({});
    const [failure, setFailure] = useState(null);
    const [pending, setPending] = useState(false);
    const [done, setDone] = useState(false);

    if (!token) {
        return (
            <div className="container-xxl py-5">
                <p className="password-change-page__notice">
                    {text.login_required} <Link to="/login">→</Link>
                </p>
            </div>
        );
    }

    const required = passwordChangeRequired(token);
    let email = null;
    try {
        email = jwtDecode(token).email || null;
    } catch {
        email = null;
    }

    const set = (field) => (event) => setValues({ ...values, [field]: event.target.value });

    const onSubmit = async (event) => {
        event.preventDefault();
        const found = {};
        if (!values.current) found.current = 'REQUIRED';
        const violation = passwordViolation(values.next, email);
        if (violation) found.next = violation;
        else if (values.next === values.current) found.next = 'PASSWORD_UNCHANGED';
        if (values.confirm !== values.next) found.confirm = 'MISMATCH';
        setErrors(found);
        setFailure(null);
        if (Object.keys(found).length) return;

        setPending(true);
        try {
            const { data } = await changePasswordApi(values.current, values.next);
            localStorage.setItem('token', data.token);
            const userData = { ...jwtDecode(data.token), token: data.token };
            localStorage.setItem('user', JSON.stringify(userData));
            dispatch(loginSuccess(userData));
            setValues({ current: '', next: '', confirm: '' });
            setDone(true);
        } catch (error) {
            const code = error?.response?.data?.error;
            if (code === 'INVALID_CURRENT_PASSWORD') setErrors({ current: code });
            else if (typeof code === 'string' && code.startsWith('PASSWORD_')) setErrors({ next: code });
            else setFailure(text.failure);
        } finally {
            setPending(false);
        }
    };

    const field = (name, label, autoComplete) => (
        <div className="password-change-page__field">
            <label htmlFor={`pwd-${name}`}>{label}</label>
            <input
                id={`pwd-${name}`}
                type="password"
                className="form-control"
                autoComplete={autoComplete}
                value={values[name]}
                onChange={set(name)}
                aria-invalid={Boolean(errors[name])}
            />
            {errors[name] && <span className="password-change-page__error" role="alert">{text.errors[errors[name]]}</span>}
        </div>
    );

    return (
        <>
            <Meta title={text.title} />
            <BreadCrumb title={text.title} />
            <div className="assiganto-home-wrapper-2 py-5">
                <div className="container-xxl">
                    <div className="auth-card password-change-page">
                        <h3 className="text-center mb-3">{text.title}</h3>
                        <p className="text-center">{text.subtitle}</p>
                        {done ? (
                            <div role="status">
                                <p className="password-change-page__success">✅ {text.success}</p>
                                <div className="d-flex justify-content-center">
                                    <button type="button" className="button" onClick={() => navigate(target, { replace: true })}>{text.continue}</button>
                                </div>
                            </div>
                        ) : (
                            <form className="d-flex flex-column gap-15" onSubmit={onSubmit} noValidate aria-busy={pending}>
                                {required && (
                                    <p className="password-change-alert password-change-alert--inline" role="alert">
                                        <strong>{text.alert_title}</strong> {text.alert_body}
                                    </p>
                                )}
                                {failure && <p className="password-change-page__error" role="alert">{failure}</p>}
                                {field('current', text.current, 'current-password')}
                                {field('next', text.new, 'new-password')}
                                <small className="password-change-page__hint">{text.rules}</small>
                                {field('confirm', text.confirm, 'new-password')}
                                <div className="d-flex justify-content-center">
                                    <button type="submit" className="button" disabled={pending}>{pending ? text.saving : text.save}</button>
                                </div>
                                {required && <Link className="text-center" to={target}>{text.later}</Link>}
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ChangePassword;
